import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { fetchApiWithToken } from '~/api/utils'
import type { DataTableQueryParams } from '~/components/DataTable/DataTable'
import {
  applyIntersectedFilterToQuery,
  getIntersectedBarAndColumnFilter,
  parseCsvFilterValue,
  setImpossibleResultFilter
} from '~/components/DataTable/queryFilterHelpers'
import type { DataTableColumnFilter } from '~/components/DataTable/types'
import type {
  FilterDefinition,
  FiltersState
} from '~/components/Filters/FilterBar/types'
import { resolveFilters } from '~/components/Filters/FilterBar/utils'
import type { ApplicationCollectionLDResponse } from '~/domains/applications/types'

const SORT_FIELD_MAP: Record<string, string> = {
  sender: 'fromName',
  isCompleted: 'isCompleted',
  offer: 'offer.title',
  company: 'offer.order.contact.company.name',
  jobboard: 'jobboard.nom',
  receivedAt: 'receivedAt',
  city: 'offer.address.city',
  user: 'offer.user.lastname'
}

const FILTER_PARAM_MAP: Record<string, string> = {
  sender: 'sender',
  isCompleted: 'isCompleted',
  offer: 'offer.title',
  company: 'company',
  jobboard: 'jobboard.nom',
  receivedAt: 'receivedAt[after]',
  city: 'offer.address.city',
  user: 'offer.user.id'
}

type ApplicationsBarFilters = FiltersState

type ApplicationStatus = 'untreated' | 'treated' | 'spam' | 'archived'

const APPLICATION_STATUS_PARAMS: Record<ApplicationStatus, string> = {
  untreated: 'pending',
  treated: 'done',
  archived: 'archived',
  spam: 'spam'
}

const isApplicationStatus = (value: string): value is ApplicationStatus =>
  Object.hasOwn(APPLICATION_STATUS_PARAMS, value)

const appendStatusFilters = (
  queryParams: URLSearchParams,
  statusValues: string[]
) => {
  for (const status of statusValues.filter(isApplicationStatus)) {
    queryParams.append(APPLICATION_STATUS_PARAMS[status], 'true')
  }
}

const appendBarFilters = (
  queryParams: URLSearchParams,
  barFilters: ApplicationsBarFilters,
  columnFilters: DataTableColumnFilter[]
) => {
  const statusValues = parseCsvFilterValue(barFilters.status)
  const tableCompletedFilter = columnFilters.find((f) => f.id === 'isCompleted')

  let effectiveStatus: string[]

  if (
    tableCompletedFilter?.value === 'true' ||
    tableCompletedFilter?.value === 'false'
  ) {
    const impliedStatuses: ApplicationStatus[] =
      tableCompletedFilter.value === 'true'
        ? ['treated', 'archived']
        : ['untreated']
    effectiveStatus =
      statusValues.length > 0
        ? statusValues.filter(
            (status) =>
              status === 'spam' ||
              impliedStatuses.includes(status as ApplicationStatus)
          )
        : impliedStatuses

    if (effectiveStatus.length === 0) {
      setImpossibleResultFilter(queryParams, 'offer.user')
      return
    }
  } else {
    effectiveStatus = statusValues
  }

  const isPendingOnly =
    effectiveStatus.length > 0 &&
    effectiveStatus.every((status) => status === 'untreated')

  const finalStatus =
    effectiveStatus.length === 0
      ? ['untreated', 'treated', 'archived']
      : isPendingOnly
        ? effectiveStatus
        : Array.from(new Set([...effectiveStatus, 'archived']))

  appendStatusFilters(queryParams, finalStatus)

  if (barFilters.spontaneous === 'only') {
    queryParams.set('exists[offer]', 'false')
  } else if (barFilters.spontaneous === 'exclude') {
    queryParams.set('exists[offer]', 'true')
  }

  if (barFilters.archivedOffer === 'only') {
    queryParams.set('archivedOffer', 'only')
  } else if (!('archivedOffer' in barFilters)) {
    queryParams.set('archivedOffer', 'all')
  }

  if (barFilters.offer) {
    queryParams.set('offer', barFilters.offer)
  }

  if (barFilters.company) {
    queryParams.set('offer.order.contact.company.id', barFilters.company)
  }

  if (barFilters.receivedAfter) {
    queryParams.set('receivedAt[after]', barFilters.receivedAfter)
  }

  if (barFilters.receivedBefore) {
    queryParams.set('receivedAt[before]', barFilters.receivedBefore)
  }

  const userFilter = getIntersectedBarAndColumnFilter({
    barFilters,
    barKey: 'users',
    columnFilters,
    columnId: 'user'
  })
  if (!applyIntersectedFilterToQuery(queryParams, 'offer.user', userFilter)) {
    return
  }

  const agencyIds = parseCsvFilterValue(barFilters.agency)
  if (agencyIds.length > 0) {
    queryParams.set('offer.agency', agencyIds.join(','))
  }
}

const buildApplicationsListSearch = (
  {
    page = 1,
    itemsPerPage = 20,
    sorting = [],
    columnFilters = []
  }: DataTableQueryParams,
  barFilters: ApplicationsBarFilters,
  filterDefinitions?: FilterDefinition[]
): string => {
  const resolvedBarFilters = filterDefinitions
    ? resolveFilters(barFilters, filterDefinitions)
    : barFilters

  const queryParams = new URLSearchParams()
  queryParams.set('page', String(page))
  queryParams.set('itemsPerPage', String(itemsPerPage))

  if (sorting.length > 0) {
    for (const sort of sorting) {
      const field = SORT_FIELD_MAP[sort.id]
      if (field) {
        queryParams.set(`order[${field}]`, sort.desc ? 'desc' : 'asc')
      }
    }
  } else {
    queryParams.set('order[receivedAt]', 'desc')
  }

  appendBarFilters(queryParams, resolvedBarFilters, columnFilters)

  for (const filter of columnFilters) {
    if (filter.id === 'isCompleted' || filter.id === 'user') {
      continue
    }
    const paramKey = FILTER_PARAM_MAP[filter.id]
    if (paramKey && filter.value) {
      queryParams.set(paramKey, String(filter.value))
    }
  }

  return queryParams.toString()
}

export const useApplicationsList = (
  dataTableParams: DataTableQueryParams = {
    page: 1,
    itemsPerPage: 20,
    sorting: [],
    columnFilters: []
  },
  barFilters: ApplicationsBarFilters = {},
  filterDefinitions?: FilterDefinition[]
) => {
  const { page, itemsPerPage, sorting, columnFilters } = dataTableParams

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [
      'applications',
      'list',
      { page, itemsPerPage, sorting, columnFilters, barFilters }
    ],
    queryFn: () =>
      fetchApiWithToken<ApplicationCollectionLDResponse>({
        method: 'get',
        url: `/v1/applications?${buildApplicationsListSearch(dataTableParams, barFilters, filterDefinitions)}`,
        useLDJson: true
      }),
    staleTime: 60_000,
    placeholderData: keepPreviousData
  })

  return {
    data: data?.['hydra:member'] ?? [],
    totalItems: data?.['hydra:totalItems'] ?? 0,
    isLoading,
    isFetching,
    isError,
    refetch
  }
}
